import { normalizePhoneToE164 } from "./phoneUtils";

const STATUS_TO_ES = {
  pending: "Pendiente",
  approved: "Aprobado",
  denied: "Rechazado",
  rechazado: "Rechazado",
  aprobado: "Aprobado",
  pendiente: "Pendiente",
};

const E164_STRICT_REGEX = /^\+\d{8,15}$/;
const IS_DEV = process.env.NODE_ENV !== "production";
const WHATSAPP_ENDPOINT_BASE = "https://api-openwa.transformaciondigitalcusco.in";

function getConfig() {
  const baseUrl = WHATSAPP_ENDPOINT_BASE;
  const internalApiKey = String(
    process.env.REACT_APP_INTERNAL_API_KEY ||
      (typeof import.meta !== "undefined" && import.meta?.env?.VITE_INTERNAL_API_KEY) ||
      ""
  ).trim();

  const defaultCountryCode = String(
    process.env.REACT_APP_DEFAULT_COUNTRY_CODE ||
      (typeof import.meta !== "undefined" && import.meta?.env?.VITE_DEFAULT_COUNTRY_CODE) ||
      ""
  ).trim();

  return { baseUrl, internalApiKey, defaultCountryCode };
}

function normalizeStatusToEs(status) {
  const key = String(status || "").trim().toLowerCase();
  return STATUS_TO_ES[key] || String(status || "").trim();
}

function sanitizeRequestId(requestId) {
  return String(requestId || "").replace(/[^a-zA-Z0-9_-]/g, "_");
}

async function fetchWhatsappStatus({ baseUrl, internalApiKey, trackingId }) {
  const endpoint = `${baseUrl.replace(/\/+$/, "")}/internal/notifications/whatsapp/${encodeURIComponent(
    trackingId
  )}`;
  const resp = await fetch(endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "x-internal-api-key": internalApiKey,
    },
  });

  const text = await resp.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!resp.ok) {
    const detail =
      (json && (json.error || json.message || json.detail)) ||
      text ||
      `HTTP ${resp.status}`;
    throw new Error(String(detail));
  }

  return json || {};
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function notifyWhatsappStatusChange({
  phone,
  status,
  userName,
  requestDate,
  requestType,
  requestId,
  idempotencyKey,
  priority = "normal",
} = {}) {
  const { baseUrl, internalApiKey, defaultCountryCode } = getConfig();

  if (!internalApiKey) {
    return { skipped: true, reason: "missing_internal_api_key" };
  }

  const normalizedPhone = normalizePhoneToE164(phone, defaultCountryCode);
  if (!normalizedPhone.ok) {
    return { skipped: true, reason: normalizedPhone.reason, code: "INVALID_PHONE" };
  }

  const phoneE164 = String(normalizedPhone.phone || "").trim();
  if (!E164_STRICT_REGEX.test(phoneE164)) {
    return { skipped: true, reason: "invalid_e164", code: "INVALID_PHONE" };
  }

  const finalStatus = normalizeStatusToEs(status);
  const stableRequestId = String(requestId || "").trim();
  if (!stableRequestId) {
    return { skipped: true, reason: "missing_request_id" };
  }

  const safeRequestId = sanitizeRequestId(stableRequestId);
  const finalIdempotencyKey = String(
    idempotencyKey || `salida:${safeRequestId}:estado:${finalStatus}:${Date.now()}`
  ).trim();

  const payload = {
    phone: phoneE164,
    status: finalStatus,
    userName: String(userName || "").trim() || "Usuario",
    requestDate: String(requestDate || "").trim(),
    requestType: String(requestType || "").trim(),
    idempotencyKey: finalIdempotencyKey,
    priority: String(priority || "normal").trim() || "normal",
  };

  const endpoint = `${baseUrl.replace(/\/+$/, "")}/internal/notifications/whatsapp`;
  if (IS_DEV) {
    const safeHeaders = {
      "Content-Type": "application/json",
      "x-internal-api-key": "***configured***",
    };
    console.info("[whatsapp][status-change] endpoint", endpoint);
    console.info("[whatsapp][status-change] headers", safeHeaders);
    console.info("[whatsapp][status-change] payload", payload);
  }

  const resp = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-internal-api-key": internalApiKey,
    },
    body: JSON.stringify(payload),
  });

  const text = await resp.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!resp.ok) {
    const detail =
      (json && (json.error || json.message || json.detail)) ||
      text ||
      `HTTP ${resp.status}`;
    throw new Error(String(detail));
  }

  const accepted = json || { ok: true };
  const trackingId = accepted?.trackingId || accepted?.jobId || accepted?.id;
  if (IS_DEV) {
    console.info("[whatsapp][status-change] trackingId", trackingId || null);
  }
  if (!trackingId) {
    return { ...accepted, deliveryStatus: "unknown" };
  }

  let lastStatus = "accepted";
  let lastPayload = null;
  for (let i = 0; i < 5; i += 1) {
    const statusResp = await fetchWhatsappStatus({ baseUrl, internalApiKey, trackingId });
    lastPayload = statusResp;

    const notification = statusResp?.notification || {};
    const currentStatus = String(notification?.status || statusResp?.status || "").trim();
    const waTo = String(notification?.waTo || "");
    const renderedMessage = String(notification?.renderedMessage || "");

    if (IS_DEV) {
      console.info("[whatsapp][status-change] poll", {
        attempt: i + 1,
        status: currentStatus || "unknown",
        waTo,
        renderedMessage,
      });
    }

    if (currentStatus) lastStatus = currentStatus;

    if (["sent", "delivered", "read"].includes(currentStatus.toLowerCase())) {
      return {
        ...accepted,
        trackingId,
        deliveryStatus: currentStatus.toLowerCase(),
        delivery: statusResp,
      };
    }

    if (["failed", "dead-letter", "rejected", "undelivered"].includes(currentStatus.toLowerCase())) {
      return {
        ...accepted,
        trackingId,
        deliveryStatus: currentStatus.toLowerCase(),
        delivery: statusResp,
      };
    }

    await sleep(2000);
  }

  return {
    ...accepted,
    trackingId,
    deliveryStatus: String(lastStatus || "retrying").toLowerCase(),
    delivery: lastPayload,
  };
}

function toRequestDateFromRow(row) {
  if (row?.requestDate) return String(row.requestDate).trim();
  const applied = String(row?.appliedDate || "").trim();
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(applied);
  if (m) return `${m[1]}/${m[2]}/${m[3]}`;
  return applied;
}

function normalizeRowStatus(row) {
  return normalizeStatusToEs(row?.newStatus || row?.status || row?.estado || "");
}

function buildPayloadFromRow(row) {
  const status = normalizeRowStatus(row);
  const requestId = String(row?.requestId || row?.leaveId || row?.id || "").trim();
  const safeRequestId = sanitizeRequestId(requestId);

  return {
    phone: row?.phone || row?.phoneNumber || "",
    status,
    userName: row?.userName || row?.usuario || "",
    requestDate: toRequestDateFromRow(row),
    requestType: row?.requestType || row?.tipoPermiso || "",
    requestId,
    idempotencyKey: `salida:${safeRequestId}:estado:${status}:${Date.now()}`,
    priority: "normal",
  };
}

export async function notifyLeaveStatusChange(row = {}) {
  try {
    const payload = buildPayloadFromRow(row);
    const postRes = await notifyWhatsappStatusChange(payload);

    if (postRes?.skipped) {
      return {
        ok: false,
        trackingId: null,
        finalStatus: "skipped",
        error: String(postRes?.reason || "skipped"),
      };
    }

    const finalStatus = String(postRes?.deliveryStatus || "accepted").toLowerCase();

    if (["sent", "delivered", "read"].includes(finalStatus)) {
      return {
        ok: true,
        trackingId: postRes?.trackingId || null,
        finalStatus,
        error: null,
      };
    }

    if (finalStatus === "dead-letter") {
      const cause = String(
        postRes?.delivery?.notification?.error ||
          postRes?.delivery?.notification?.deadLetterReason ||
          postRes?.delivery?.error ||
          ""
      ).trim();

      return {
        ok: false,
        trackingId: postRes?.trackingId || null,
        finalStatus: "dead-letter",
        error:
          "No se pudo enviar WhatsApp (n�mero inv�lido/no resolvible)" +
          (cause ? `: ${cause}` : ""),
      };
    }

    return {
      ok: false,
      trackingId: postRes?.trackingId || null,
      finalStatus,
      error: null,
    };
  } catch (err) {
    return {
      ok: false,
      trackingId: null,
      finalStatus: "error",
      error: err?.message || "Error desconocido",
    };
  }
}
